import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart, cartTotals, suggestPackage } from "@/lib/cart";
import { useQuery } from "@tanstack/react-query";
import { fetchPackages } from "@/lib/queries";
import { money } from "@/lib/format";
import { Trash2, Package as PackageIcon, Sparkles, ShoppingCart } from "lucide-react";
import { Link, useLocation } from "@tanstack/react-router";
import { Disclaimer } from "./Disclaimer";
import { getPackageStyle } from "@/lib/package-styles";
import { trackBeginCheckout } from "@/lib/analytics";

export function CartDrawer() {
  const { items, open, setOpen, remove, addPackage } = useCart();
  const { data: packages = [] } = useQuery({ queryKey: ["packages"], queryFn: fetchPackages });
  const { subtotal, total } = cartTotals(items);
  const suggestion = suggestPackage(items, packages);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col bg-surface p-0 sm:max-w-md">
        <SheetHeader className="flex flex-row items-center justify-between border-b border-hairline px-5 py-4">
          <SheetTitle className="flex items-center gap-2 text-foreground">
            <ShoppingCart className="h-4 w-4 text-primary" /> Tu carrito
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="grid h-full place-items-center text-center">
              <div>
                <ShoppingCart className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-3 text-sm text-muted-foreground">Tu carrito está vacío.</p>
                <Link to="/paquetes" onClick={() => setOpen(false)}>
                  <Button className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90">Ver paquetes</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((i) => {
                const pkgStyle = i.type === "package" ? getPackageStyle(i.slug) : null;
                return (
                  <div
                    key={i.id}
                    className={`rounded-md border p-3 ${
                      pkgStyle ? `${pkgStyle.cardBorder} bg-surface` : "border-hairline bg-surface-2"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {i.type === "package" && pkgStyle && (
                            <span
                              className={`inline-flex items-center gap-1 rounded ${pkgStyle.accentBg} px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${pkgStyle.badgeText}`}
                            >
                              <PackageIcon className="h-3 w-3" /> Paquete
                            </span>
                          )}
                          <span className="truncate text-sm font-medium text-foreground">{i.name}</span>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">{money(i.price)}</div>
                        {i.type === "package" && (
                          <div className="mt-2 text-[11px] text-muted-foreground">
                            Incluye {i.includedServiceIds.length} servicios
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => remove(i.id)}
                        className="cursor-pointer rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                        aria-label="Quitar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {suggestion && (() => {
                const sugStyle = getPackageStyle(suggestion.pkg.slug);
                return (
                  <div className={`rounded-md border ${sugStyle.cardBorder} ${sugStyle.accentBg} p-3`}>
                    <div className="flex items-start gap-2">
                      <Sparkles className={`mt-0.5 h-4 w-4 ${sugStyle.iconClass}`} />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-foreground">
                          {suggestion.mode === "upgrade-from-package"
                            ? `Te conviene cambiar a ${suggestion.pkg.name}`
                            : `Te conviene más el paquete ${suggestion.pkg.name}`}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          Ahorras {money(suggestion.saves)} cambiando a este paquete.
                        </div>
                        <Button
                          size="sm"
                          className={`mt-3 ${sugStyle.buttonClass}`}
                          onClick={() =>
                            addPackage({
                              id: suggestion.pkg.id,
                              slug: suggestion.pkg.slug,
                              name: suggestion.pkg.name,
                              price: Number(suggestion.pkg.price),
                              includedServiceIds: suggestion.pkg.service_ids,
                            })
                          }
                        >
                          Cambiar al paquete
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <Disclaimer compact />
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-hairline bg-surface px-5 py-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Subtotal</span>
              <span>{money(subtotal)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-base font-semibold text-foreground">
              <span>Total estimado</span>
              <span>{money(total)}</span>
            </div>
            <Link
              to="/checkout"
              onClick={() => {
                trackBeginCheckout(
                  items.map((i) => ({ id: i.id, slug: i.slug, name: i.name, price: Number(i.price), type: i.type })),
                  total
                );
                setOpen(false);
              }}
            >
              <Button size="lg" className="mt-4 w-full bg-primary text-primary-foreground hover:bg-primary/90">
                Continuar al pago seguro
              </Button>
            </Link>
            <p className="mt-2 text-center text-[11px] text-muted-foreground">
              El total mostrado corresponde a los servicios seleccionados. Algunos fees oficiales fijos pueden estar incluidos. Costos variables (seguro, suscripciones, impresión, envío, etc.) se cotizan o pagan aparte si aplican.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** Mobile-only sticky bottom summary */
export function MobileCartBar() {
  const { items, setOpen } = useCart();
  const { total } = cartTotals(items);
  const { pathname } = useLocation();
  // Gate to client-only render: zustand-persist hydrates items from localStorage
  // synchronously, causing SSR (empty) vs client (filled) mismatch (React #418).
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  if (!mounted) return null;
  if (items.length === 0) return null;
  const HIDDEN_PREFIXES = ["/checkout", "/intake", "/gracias", "/cuenta", "/admin"];
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-hairline bg-surface/95 px-4 py-3 backdrop-blur md:hidden">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Carrito · {items.length}</div>
          <div className="text-base font-semibold text-foreground">{money(total)}</div>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90">
          Continuar al pago seguro
        </Button>
      </div>
    </div>
  );
}
